// Pure, UI-agnostic model builder for the environment-aware Clients view
// (DEV-6578). One ClientGroup per org; each group's subRows come from that
// org's non-disabled environments (or a single env: null row when it has
// none). Per sub-row, an env-exact HubSpot instance is preferred over the
// org's any-env HubSpot instance, which is shared across every sub-row.

export interface EnvSubRow {
  env: string | null;
  hubspot?: { portalId?: string | number; shared: boolean };
  connected: boolean;
}

export interface ClientGroup {
  org: { id: string; name: string };
  portal?: string;
  envCount: number;
  subRows: EnvSubRow[];
}

interface ConnectorInstanceInput {
  id: string;
  organizationId: string;
  connectorType: string;
  environment?: string | null;
  config?: Record<string, unknown>;
}

interface EnvironmentInput {
  name: string;
  organizationId: string;
  disabled?: boolean;
}

interface OrgInput {
  id: string;
  name: string;
}

function portalIdOf(instance: ConnectorInstanceInput | undefined): string | number | undefined {
  const raw = instance?.config?.portalId;
  return typeof raw === "string" || typeof raw === "number" ? raw : undefined;
}

export function buildClientGroups(
  orgs: OrgInput[],
  instances: ConnectorInstanceInput[],
  environments: EnvironmentInput[],
): ClientGroup[] {
  return orgs.map((org) => {
    const orgEnvironments = environments.filter(
      (e) => e.organizationId === org.id && !e.disabled,
    );

    const orgHubspotInstances = instances.filter(
      (i) => i.organizationId === org.id && i.connectorType === "hubspot",
    );
    const anyEnvHubspot = orgHubspotInstances.find((i) => i.environment == null);

    const envNames = orgEnvironments.length > 0 ? orgEnvironments.map((e) => e.name) : [null];

    const subRows: EnvSubRow[] = envNames.map((env) => {
      const envExactHubspot =
        env != null ? orgHubspotInstances.find((i) => i.environment === env) : undefined;
      const resolved = envExactHubspot ?? anyEnvHubspot;

      if (!resolved) {
        return { env, connected: false };
      }

      return {
        env,
        connected: true,
        hubspot: {
          portalId: portalIdOf(resolved),
          shared: envExactHubspot == null,
        },
      };
    });

    const groupHubspot = orgHubspotInstances.find((i) => i.environment != null) ?? anyEnvHubspot;
    const groupPortalId = portalIdOf(groupHubspot);

    return {
      org: { id: org.id, name: org.name },
      portal: groupPortalId != null ? String(groupPortalId) : undefined,
      envCount: orgEnvironments.length,
      subRows,
    };
  });
}
